import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import {
  PhotoCamera as CameraIcon, UploadFile as FileIcon, PictureAsPdf as PdfIcon, Image as ImageIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
export const REPORT_TYPE_LABEL = {
  blood: 'Blood test', urine: 'Urine test', imaging: 'Imaging / scan', ecg: 'ECG',
  pathology: 'Pathology', prescription: 'Prescription', letter: 'Letter', other: 'Other',
};
export const BLOOD_TEST_LABEL = {
  cbc: 'Complete blood count (CBC)', lipid: 'Lipid profile', hba1c: 'HbA1c', glucose: 'Blood sugar',
  lft: 'Liver function (LFT)', kft: 'Kidney function (KFT)', thyroid: 'Thyroid (TSH, T3, T4)',
  vitamin_d: 'Vitamin D', vitamin_b12: 'Vitamin B12', iron: 'Iron studies', electrolytes: 'Electrolytes',
  crp_esr: 'Inflammation (CRP / ESR)', other: 'Other blood test',
};
/** "Blood test · Lipid profile", or just the type. */
export const reportKindLabel = (r) => [REPORT_TYPE_LABEL[r.report_type] || r.report_type || 'Report',
  r.report_type === 'blood' && r.blood_test ? BLOOD_TEST_LABEL[r.blood_test] || null : null].filter(Boolean).join(' · ');
const MAX_BYTES = 15 * 1024 * 1024;
const MAX_SIDE = 2400;       // plenty to read a lab report; a phone photo is ~4000px

/**
 * Photos from a phone camera are 4–10 MB. Re-encode them as JPEG at a readable size
 * before upload. PDFs, and formats the browser can't draw (HEIC outside Safari), go
 * as they are — the server accepts them and enforces the 15 MB cap.
 */
export async function shrinkPhoto(file) {
  if (!file.type.startsWith('image/') || file.size < 1.5 * 1024 * 1024) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

/**
 * Add a report for a patient. Pass `patient` to fix the patient (from their record),
 * or leave it out to choose one. The visit is optional.
 */
export default function ReportUpload({ open, onClose, onSaved, patient: fixedPatient, sessionId }) {
  const [patients, setPatients] = useState([]);
  const [patient, setPatient] = useState(fixedPatient || null);
  const [visits, setVisits] = useState([]);
  const [form, setForm] = useState({ title: '', report_type: 'blood', blood_test: '', report_date: '', notes: '', session_id: sessionId || '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const camera = useRef(null);
  const picker = useRef(null);

  useEffect(() => {
    if (!open) return;
    setPatient(fixedPatient || null);
    setForm({ title: '', report_type: 'blood', blood_test: '', report_date: '', notes: '', session_id: sessionId || '' });
    setFile(null);
    if (!fixedPatient) {
      axios.get(`${API_URL}/api/v1/clinical/patients`)
        .then(({ data }) => setPatients((data.patients || []).map((p) => ({ id: p.patient_id, name: p.name, dob: p.dob }))))
        .catch(() => setPatients([]));
    }
  }, [open, fixedPatient, sessionId]);

  useEffect(() => {
    setVisits([]);
    if (!open || !patient?.id) return;
    axios.get(`${API_URL}/api/v1/reports/patients/${patient.id}/visits`)
      .then(({ data }) => setVisits(data.visits || []))
      .catch(() => setVisits([]));
  }, [open, patient]);

  const choose = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const ready = await shrinkPhoto(f);
    if (ready.size > MAX_BYTES) { toast.error('That file is over 15 MB. Please use a smaller one.'); return; }
    setFile(ready);
    if (!form.title) setForm((s) => ({ ...s, title: f.name.replace(/\.[^.]+$/, '').slice(0, 80) }));
  };

  const save = async () => {
    if (!patient || !file || !form.title.trim()) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append('patient_id', patient.id);
      body.append('file', file);
      Object.entries(form).forEach(([k, v]) => { if (v) body.append(k, v); });
      await axios.post(`${API_URL}/api/v1/reports`, body);
      toast.success('Report added');
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add the report');
    } finally {
      setBusy(false);
    }
  };

  // A new type clears the blood test: only a blood report has one.
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value, ...(k === 'report_type' ? { blood_test: '' } : {}) });

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Add a report</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {fixedPatient ? (
            <Typography variant="body2">Patient: <b>{fixedPatient.name}</b></Typography>
          ) : (
            <Autocomplete options={patients} value={patient} onChange={(e, v) => setPatient(v)}
              getOptionLabel={(p) => p.name || ''} isOptionEqualToValue={(a, b) => a.id === b.id}
              renderOption={(props, p) => (
                <Box component="li" {...props} key={p.id}>
                  <Box><Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                    <Typography variant="caption" color="text.secondary">DOB {p.dob || '—'}</Typography></Box>
                </Box>
              )}
              renderInput={(params) => <TextField {...params} label="Patient" placeholder="Search by name" required />} />
          )}

          <Box sx={{ p: 2, border: '1px dashed', borderColor: file ? 'primary.main' : 'divider', borderRadius: 2, textAlign: 'center' }}>
            {file ? (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                {file.type === 'application/pdf' ? <PdfIcon color="error" /> : <ImageIcon color="primary" />}
                <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>{file.name}</Typography>
                <Typography variant="caption" color="text.secondary">{(file.size / 1024 / 1024).toFixed(1)} MB</Typography>
                <Button size="small" onClick={() => setFile(null)}>Change</Button>
              </Stack>
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
                <Button variant="outlined" startIcon={<CameraIcon />} onClick={() => camera.current?.click()}>Take photo</Button>
                <Button variant="outlined" startIcon={<FileIcon />} onClick={() => picker.current?.click()}>Choose PDF or image</Button>
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              PDF or photo, up to 15 MB. Photos are resized for you.
            </Typography>
            {/* capture opens the camera directly on a phone; on a computer it's a file picker. */}
            <input ref={camera} type="file" accept="image/*" capture="environment" hidden onChange={choose} />
            <input ref={picker} type="file" accept="application/pdf,image/*" hidden onChange={choose} />
          </Box>

          <TextField label="Title" value={form.title} onChange={set('title')} required placeholder="e.g. Full blood count" />
          <Stack direction="row" spacing={2}>
            <TextField select label="Type" value={form.report_type} onChange={set('report_type')} sx={{ flex: 1 }}>
              {Object.entries(REPORT_TYPE_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
            <TextField type="date" label="Report date" InputLabelProps={{ shrink: true }}
              value={form.report_date} onChange={set('report_date')} sx={{ flex: 1 }} />
          </Stack>
          {form.report_type === 'blood' && (
            <TextField select label="Blood test" value={form.blood_test} onChange={set('blood_test')}
              helperText="Which test this is, so it can be found by test later.">
              <MenuItem value=""><em>Not specified</em></MenuItem>
              {Object.entries(BLOOD_TEST_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </TextField>
          )}
          <TextField select label="Link to a visit (optional)" value={form.session_id} onChange={set('session_id')}
            disabled={!patient} helperText={patient && !visits.length ? 'This patient has no completed visits yet.' : ' '}>
            <MenuItem value=""><em>Not linked to a visit</em></MenuItem>
            {visits.map((v) => (
              <MenuItem key={v.session_id} value={v.session_id}>{v.date} — {v.doctor_name || 'Visit'}</MenuItem>
            ))}
          </TextField>
          <TextField label="Notes (optional)" value={form.notes} onChange={set('notes')} multiline minRows={2} />
          {!patient && !fixedPatient && <Alert severity="info">Choose the patient first.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || !patient || !file || !form.title.trim()}
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : null}>
          {busy ? 'Uploading…' : 'Add report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
